import { useTranslation } from 'react-i18next';
import { ButtonVariantEnum, ErrorEnum, JxtButton, JxtError } from '@jooxter/ui';
import { HOME_ROUTE } from '@jooxter/core';
import { CookieService } from '@jooxter/storage';

const HOME_URL = '/';

/**
 * Rendered by the root `Sentry.ErrorBoundary` instead of a blank page when the React tree
 * crashes (ND-5676).
 *
 * It sits above the router, so navigation goes through `window.location`: a full reload also
 * clears whatever state caused the crash. "Back to home" is the way out when the crash is
 * deterministic on the current URL and reloading would just loop.
 * Every string carries a `defaultValue`: the translation bundle is fetched remotely and may
 * well be what is missing when we get here.
 */
export const JxtErrorFallback = () => {
  const { t } = useTranslation();
  const isHome = window.location.pathname === HOME_ROUTE;

  const reload = () => window.location.reload();

  const reloadWithoutCache = async () => {
    localStorage.clear();
    await CookieService.deleteAll();
    reload();
  };

  const navigateToHome = () => window.location.assign(HOME_URL);

  return (
    <div className="bg-background min-h-screen flex justify-center items-center">
      <JxtError className="m-4" code={ErrorEnum.APP_CRASH}>
        <JxtButton onClick={() => window.location.reload()}>
          {t('error.reload-page', { defaultValue: 'Reload the page' })}
        </JxtButton>
        {!isHome && (
          <JxtButton variant={ButtonVariantEnum.Link} onClick={() => navigateToHome()}>
            {t('error.back-to-home', { defaultValue: 'Back to home' })}
          </JxtButton>
        )}
        <JxtButton
          title={t<string>('error.back-to-home.title', { defaultValue: 'This will log you out of the application' })}
          variant={ButtonVariantEnum.Link}
          onClick={() => reloadWithoutCache()}
        >
          {t('error.reload-page-without-cache', { defaultValue: 'Reload the page without cache' })}
          <i className="fas ml-1 fa-question-circle" />
        </JxtButton>
      </JxtError>
    </div>
  );
};
